import type { ReactNode } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Modal, ModalContent, ModalStyle } from '@amzn/stencil-react-components/modal';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';

interface ActionModalProps {
  isOpen: boolean;
  /** Modal title, also used as the dialog aria-label. */
  titleText: string;
  /** Optional always-visible informational banner above the content. */
  infoText?: string | undefined;
  /**
   * Error to display (validation or API). Callers show validation errors
   * first, falling back to API errors — only one shows at a time.
   * Explicit `| undefined` so callers can pass computed values directly
   * under exactOptionalPropertyTypes.
   */
  errorMessage?: string | undefined;
  /** Disables both buttons and swaps the confirm label while true. */
  isProcessing: boolean;
  confirmLabel: string;
  processingLabel: string;
  closeLabel: string;
  /** Render as a bottom sheet (mobile pattern for field-heavy modals). */
  bottomSheet?: boolean;
  /**
   * Optional stable testid for the confirm CTA. When provided, renders
   * as `data-testid` on the Primary button so external suites
   * (Playwright) can target it without relying on translated labels.
   * Consumers that host multiple modals in the DOM at once should
   * pass unique values to keep locators strict-mode safe.
   */
  confirmTestId?: string | undefined;
  /** Stable testid for the close/cancel CTA — same contract as
   *  {@link ActionModalProps.confirmTestId}. */
  closeTestId?: string | undefined;
  onClose: () => void;
  /** Confirm click — run validation here and only proceed when valid. */
  onConfirm: () => void;
  /** Modal body: confirmation text or form fields. */
  children?: ReactNode;
}

/**
 * Shared confirm/form modal shell used by feature action modals
 * (cancel, reopen, reassign, change due date, ...).
 *
 * Owns the recurring chrome: title, optional info banner, single
 * error banner, and the close/confirm footer with processing state.
 * Feature modals own their field state, validation, and translations.
 */
export function ActionModal({
  isOpen,
  titleText,
  infoText,
  errorMessage,
  isProcessing,
  confirmLabel,
  processingLabel,
  closeLabel,
  bottomSheet = false,
  confirmTestId,
  closeTestId,
  onClose,
  onConfirm,
  children,
}: ActionModalProps): JSX.Element {
  return (
    <Modal
      isOpen={isOpen}
      close={onClose}
      aria-label={titleText}
      {...(bottomSheet ? { style: ModalStyle.BottomSheet } : {})}
    >
      <ModalContent titleText={titleText} onCloseButtonClick={onClose}>
        <Col gridGap="dimensions.spacing.300" padding="dimensions.spacing.200">
          {infoText && (
            <MessageBanner type={MessageBannerType.Informational} isDismissible={false}>
              {infoText}
            </MessageBanner>
          )}

          {errorMessage && (
            <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
              {errorMessage}
            </MessageBanner>
          )}

          {children}

          <Row gridGap="dimensions.spacing.200" justifyContent="flex-end">
            <Button
              variant={ButtonVariant.Secondary}
              onClick={onClose}
              disabled={isProcessing}
              data-testid={closeTestId}
            >
              {closeLabel}
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              onClick={onConfirm}
              disabled={isProcessing}
              data-testid={confirmTestId}
            >
              {isProcessing ? processingLabel : confirmLabel}
            </Button>
          </Row>
        </Col>
      </ModalContent>
    </Modal>
  );
}
